/**
 * Tenant session bootstrap + switching (Phase 8).
 *
 * Backend flow (strategy §4.2):
 *   login  → JWT with no tenant context
 *   GET  /api/secure/tenant/memberships → which tenants the user belongs to
 *   POST /api/secure/tenant/switch      → new JWT carrying the `act` claim
 *   GET  /api/secure/tenant/current     → effective roles + permission set
 *
 * A single-membership user is auto-resolved server-side, so `current` returns
 * their context directly; a multi-membership / ROOT user gets 204 until they
 * pick a tenant, which we do automatically on login (first membership) so the
 * permission set is available immediately. The TenantSwitcher lets them change.
 */

import { apiJson } from './ApiClient';
import { applySession } from './session';

export const fetchMemberships = async (token) => {
  const res = await apiJson('/api/secure/tenant/memberships', { token });
  return res.ok && Array.isArray(res.data) ? res.data : [];
};

export const fetchCurrentContext = async (token) => {
  // 204 (no active tenant) → apiJson returns ok:true, data:null.
  const res = await apiJson('/api/secure/tenant/current', { token });
  return res.ok ? res.data : null;
};

export const switchTenant = async (tenantId, token) => {
  const res = await apiJson('/api/secure/tenant/switch', {
    method: 'POST',
    body: { tenantId },
    token,
  });
  return res; // { ok, status, data:{ token, tenantId, slug, platformScope, crossTenant }, error }
};

/**
 * Merge a resolved tenant context into the in-memory session and return the
 * fields to fold into `loginUser` (permissions, roles, active tenant, ...).
 * `token` is the JWT that should ride subsequent requests.
 */
const commit = (token, tenantContext, memberships) => {
  const permissions = (tenantContext && Array.from(tenantContext.permissions || [])) || [];
  const ctxRoles = (tenantContext && Array.from(tenantContext.roles || [])) || [];
  const tenantId = tenantContext ? tenantContext.tenantId : null;

  applySession({ token, tenantId, permissions, roles: ctxRoles, memberships, tenantContext });

  return {
    jwtToken: token,
    permissions,
    tenantPermissions: permissions,
    activeTenantId: tenantId,
    activeTenantSlug: tenantContext ? tenantContext.slug : null,
    platformScope: !!(tenantContext && tenantContext.platformScope),
    crossTenant: !!(tenantContext && tenantContext.crossTenant),
    memberships,
    tenantContext,
  };
};

/**
 * Run the full post-login bootstrap. Returns an object to spread into
 * `loginUser` (or `{}` if the user has no tenant / the calls fail — the app
 * then degrades to the pre-P8 role-only behaviour). Never throws.
 */
export const bootstrapTenantSession = async (loginToken) => {
  try {
    const memberships = await fetchMemberships(loginToken);
    let token = loginToken;
    let context = await fetchCurrentContext(loginToken);

    // Multi-membership / ROOT: no active tenant yet → adopt the first membership.
    if (!context && memberships.length > 0) {
      const first = memberships[0];
      const sw = await switchTenant(first.tenantId, loginToken);
      if (sw.ok && sw.data && sw.data.token) {
        token = sw.data.token;
        context = await fetchCurrentContext(token);
      }
    }

    if (!context) {
      // Keep the login token; nothing tenant-scoped to add.
      applySession({ token: loginToken, memberships });
      return { memberships };
    }
    return commit(token, context, memberships);
  } catch (e) {
    console.log('tenant bootstrap failed: ' + (e && e.message));
    applySession({ token: loginToken });
    return {};
  }
};

/**
 * Switch the active tenant (used by the TenantSwitcher). Returns the fields to
 * fold into `loginUser`, or null on failure.
 */
export const applyTenantSwitch = async (tenantId, currentToken, memberships) => {
  const sw = await switchTenant(tenantId, currentToken);
  if (!sw.ok || !sw.data || !sw.data.token) return null;
  const context = await fetchCurrentContext(sw.data.token);
  if (!context) return null;
  return commit(sw.data.token, context, memberships);
};

/**
 * Re-fetch the effective roles/permissions for the CURRENT tenant using the
 * existing JWT — the same `fetchCurrentContext → commit` path as a tenant
 * switch, minus the POST /switch step (no tenant change, no new token). Lets
 * the menu/route guards catch up after ROOT edits a role's permissions, with
 * no re-login.
 *
 * FAIL SAFE: on a network error, a non-2xx (e.g. expired token → 401), or an
 * empty context, this returns null and leaves the in-memory session untouched
 * — the caller treats null as a no-op and keeps the existing permissions. It
 * never clears the session or logs the user out.
 *
 * Returns the same shape `applyTenantSwitch` returns (spreadable into
 * `loginUser`) on success, or null.
 */
export const refreshTenantSession = async (existingJwt, memberships) => {
  if (!existingJwt) return null;
  try {
    const context = await fetchCurrentContext(existingJwt);
    if (!context) return null; // no active tenant / 204 / non-2xx → no-op
    return commit(existingJwt, context, memberships);
  } catch (e) {
    console.warn('tenant refresh failed: ' + (e && e.message));
    return null;
  }
};
